import { useMemo, useState } from 'react';
import { RaporCekmecesi } from '../rapor-ekrani/RaporCekmecesi';
import { RaporTablosu } from '../rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../rapor-ekrani/RaporHatasi';
import { FILTRE_GIRIS, FiltreAlani, gecerliTarih } from '../rapor-ekrani/RaporFiltreBari';
import { useRaporSorgu } from '../rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../rapor-ekrani/useDarEkran';
import { borcAlacak, paraMetni, tarihMetni } from '../rapor-ekrani/bicim';
import type { RaporKolonu } from '../rapor-ekrani/kolon';
import type { PanelEkranKaynagi } from '../../types/panelConfig';
import { BaIsareti } from './BaIsareti';
import { HAREKET_ESIGI, hareketiCokMu, hareketOzeti, type CariSatiri, type HamHareketSatiri, type HareketSatiri } from './tipler';

interface Props {
  /** Açık cari; `undefined` ise çekmece kapalıdır. */
  cari: CariSatiri | undefined;
  kaynak: PanelEkranKaynagi | undefined;
  /** Listedeki "şu tarihe kadar": bitiş tarihinin varsayılanı. */
  sonTarih: string;
  onKapat: () => void;
}

/**
 * S334: carinin hareketleri, sağdan açılan çekmecede. İlk satır "Devreden" (dönem öncesi bakiye),
 * altta yapışık "Dönem toplamı (N hareket)"; Bakiye toplamı son hareketin yürüyen bakiyesidir.
 */
export function CariHareketCekmecesi({ cari, kaynak, sonTarih, onKapat }: Props) {
  const alt = cari
    ? [cari.kod, cari.temsilci && `Temsilci ${cari.temsilci}`, cari.vade !== null && `Vade ${cari.vade} gün`]
        .filter(Boolean)
        .join(' · ')
    : undefined;
  return (
    <RaporCekmecesi acik={!!cari} baslik={cari?.ad ?? ''} altBaslik={alt} onKapat={onKapat} testid="cari-cekmece">
      {/* `key`: başka cariye geçince tarihler varsayılana döner ve önceki carinin satırları taşınmaz. */}
      {cari && (hareketiCokMu(cari)
        ? <CokHareket cari={cari} sonTarih={sonTarih} />
        : <Icerik key={cari.id} cari={cari} kaynak={kaynak} sonTarih={sonTarih} />)}
    </RaporCekmecesi>
  );
}

function Kutu({ etiket, children }: { etiket: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-border rounded-lg px-3 py-2 min-w-[120px]">
      <span className="block text-[11px] uppercase tracking-[0.04em] text-ink-500">{etiket}</span>
      <b className="mono text-[15px] font-semibold text-ink-900 leading-snug">{children}</b>
    </div>
  );
}

const SAYI = new Intl.NumberFormat('tr-TR');

/**
 * S342: eşiğin üstündeki caride hareket ds'i HİÇ sorgulanmaz (tarih daraltmak da kurtarmaz: devreden
 * için carinin bütün geçmişi okunur). Çekmece nedenini söyler ve listedeki toplamları gösterir.
 */
function CokHareket({ cari, sonTarih }: { cari: CariSatiri; sonTarih: string }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3 p-4">
      <div className="flex flex-wrap gap-2.5" data-testid="cari-ozet">
        <Kutu etiket="Borç">{paraMetni(cari.borc)}</Kutu>
        <Kutu etiket="Alacak">{paraMetni(cari.alacak)}</Kutu>
        <Kutu etiket="Bakiye">{paraMetni(cari.bakiye)} <BaIsareti deger={cari.ba} /></Kutu>
      </div>
      <div
        data-testid="cari-cekmece-cok-hareket"
        className="bg-surface border border-border rounded-lg p-6 text-[13px] text-ink-700 leading-relaxed"
      >
        <p className="m-0 font-semibold text-ink-900">Bu carinin hareket dökümü açılmıyor</p>
        <p className="m-0 mt-1">
          Caride {SAYI.format(cari.hareketSayisi ?? 0)} hareket var. Hareket dökümü{' '}
          {SAYI.format(HAREKET_ESIGI)} hareketin üstündeki carilerde gösterilmez. Yukarıdaki toplamlar{' '}
          {tarihMetni(sonTarih)} tarihine kadarki borç, alacak ve bakiyedir.
        </p>
      </div>
    </div>
  );
}

function Icerik({ cari, kaynak, sonTarih }: { cari: CariSatiri; kaynak: PanelEkranKaynagi | undefined; sonTarih: string }) {
  const telefon = useDarEkran();
  // Varsayılan dönem: "şu tarihe kadar"ın yıl başından o tarihe.
  const [bas, setBas] = useState(`${sonTarih.slice(0, 4)}-01-01`);
  const [bit, setBit] = useState(sonTarih);
  const [basGiris, setBasGiris] = useState(bas);
  const [bitGiris, setBitGiris] = useState(bit);
  const ters = bas > bit;

  const q = useRaporSorgu<HamHareketSatiri>(kaynak, { '@CARI': cari.id, '@BASTARIH': bas, '@BITTARIH': bit }, !ters);
  const ozet = useMemo(() => hareketOzeti(q.data ?? []), [q.data]);

  // `kart`: telefonda hareket kartı. Üstte tarih + işlem, sağında tutar; altında belge no, açıklama, vade
  // ve sağında yürüyen bakiye.
  const kolonlar = useMemo<RaporKolonu<HareketSatiri>[]>(() => [
    { id: 'tarih', baslik: 'Tarih', tur: 'tarih', deger: (h) => h.tarih, kart: 'baslik' },
    { id: 'islem', baslik: 'İşlem', tur: 'metin', deger: (h) => h.islem, kart: 'baslik' },
    { id: 'belge_no', baslik: 'Belge No', tur: 'kod', deger: (h) => h.belgeNo, kart: 'alt' },
    { id: 'aciklama', baslik: 'Açıklama', tur: 'metin', deger: (h) => h.aciklama, esnek: true, kart: 'alt' },
    { id: 'vade', baslik: 'Vade', tur: 'tarih', deger: (h) => h.vade, kart: 'alt', kartEtiketi: 'Vade' },
    // Sıfır tutar boş hücredir (borç satırında alacak kolonu "₺0,00" ile dolmasın).
    { id: 'borc', baslik: 'Borç', tur: 'para', deger: (h) => h.borc || null, toplam: () => ozet.donemBorc, kart: 'olcu' },
    { id: 'alacak', baslik: 'Alacak', tur: 'para', deger: (h) => h.alacak || null, toplam: () => ozet.donemAlacak, kart: 'olcu' },
    // Yürüyen bakiyenin "toplamı" toplam değildir: dönemin SON bakiyesidir (sıralama değişse de).
    { id: 'bakiye', baslik: 'Bakiye', tur: 'para', deger: (h) => h.yuruyen, toplam: () => ozet.bakiye, kart: 'olcuAlt' },
  ], [ozet]);

  const devredenSatiri = useMemo<HareketSatiri[]>(() => [{
    sira: 0, tarih: null, islem: 'Devreden', belgeNo: '', aciklama: `${tarihMetni(bas)} öncesi bakiye`,
    vade: null, borc: 0, alacak: 0, yuruyen: ozet.devreden,
  }], [bas, ozet.devreden]);

  const tarihAlani = (etiket: string, giris: string, setGiris: (v: string) => void, uygula: (v: string) => void, gecerli: string) => (
    <FiltreAlani etiket={etiket}>
      <input
        type="date"
        className={telefon ? `${FILTRE_GIRIS} w-full` : FILTRE_GIRIS}
        value={giris}
        onChange={(e) => {
          setGiris(e.target.value);
          if (gecerliTarih(e.target.value)) uygula(e.target.value);
        }}
        onBlur={() => { if (!gecerliTarih(giris)) setGiris(gecerli); }}
      />
    </FiltreAlani>
  );

  const ilkYukleme = q.isLoading;
  return (
    <>
      {/* Telefonda iki tarih yan yana, eşit genişlikte (360 px'te alt alta düşüyordu). */}
      <div
        className={`bg-surface border-b border-border px-4 py-2 ${telefon ? 'grid grid-cols-2 gap-x-3' : 'flex flex-wrap items-end gap-x-3.5 gap-y-2'}`}
        data-testid="cari-cekmece-filtre"
      >
        {tarihAlani('Başlangıç', basGiris, setBasGiris, setBas, bas)}
        {tarihAlani('Bitiş', bitGiris, setBitGiris, setBit, bit)}
      </div>
      {/* Telefon: gövde kısalmaz, pencere tek kaydırmayla kayar; alt dolgu yok (toplam şeridi dipte). */}
      <div className={telefon ? 'flex-1 flex flex-col gap-2 px-4 pt-3 rk-kenar-16' : 'flex-1 min-h-0 flex flex-col gap-3 p-4'}>
        {ters ? (
          <RaporHatasi testid="cari-cekmece-ters" mesaj="Başlangıç tarihi bitiş tarihinden sonra olamaz." />
        ) : !kaynak ? (
          <RaporHatasi testid="cari-cekmece-kaynaksiz" mesaj="Bu firmada cari hareketleri kaynağı tanımlı değil." />
        ) : q.isError ? (
          <RaporHatasi testid="cari-cekmece-hata" mesaj="Cari hareketleri yüklenemedi." onTekrar={() => q.refetch()} />
        ) : (
          <>
            {/* Telefonda özet kutuları YOK: Devreden ilk kartta, dönem borç / alacak / bakiye alttaki şeritte
                yazıyor (sayı ekranda tek yerde, Yerlice belge detayı S300). */}
            {!telefon && (
            <div className="flex flex-wrap gap-2.5" data-testid="cari-ozet" aria-busy={q.isFetching}>
              <Kutu etiket="Devreden">{ilkYukleme ? '…' : paraMetni(ozet.devreden)}</Kutu>
              <Kutu etiket="Dönem borç">{ilkYukleme ? '…' : paraMetni(ozet.donemBorc)}</Kutu>
              <Kutu etiket="Dönem alacak">{ilkYukleme ? '…' : paraMetni(ozet.donemAlacak)}</Kutu>
              <Kutu etiket="Bakiye">
                {ilkYukleme ? '…' : <>{paraMetni(ozet.bakiye)} <BaIsareti deger={borcAlacak(ozet.bakiye)} /></>}
              </Kutu>
            </div>
            )}
            <RaporTablosu
              testid="cari-hareket-tablosu"
              etiket={`${cari.ad} hareketleri`}
              className={telefon ? '' : 'min-h-[140px]'}
              kartStili="cizgi"
              kolonlar={kolonlar}
              satirlar={ozet.hareketler}
              satirKimligi={(h) => h.sira}
              ustSatirlar={devredenSatiri}
              toplamEtiketi={`Dönem toplamı (${ozet.hareketler.length} hareket)`}
              yukleniyor={ilkYukleme}
              yenileniyor={q.isFetching && !ilkYukleme}
            />
          </>
        )}
      </div>
    </>
  );
}
